import { Client, cacheExchange, subscriptionExchange, fetchExchange } from '@urql/core';
import { retryExchange } from '@urql/exchange-retry';
import { setContextClient, queryStore as urqlQueryStore, mutationStore as urqlMutationStore } from '@urql/svelte';
import axios from 'axios';
import { createClient } from 'graphql-ws';
import { writable } from 'svelte/store';
import { join, intersection, isString } from 'lodash-es';
import navigatorLanguages from 'navigator-languages';

// AXIOS API
export const api = axios.create({
	baseURL: window.location.origin,
	withCredentials: true,
	timeout: 10000
});

api.interceptors.response.use(undefined, async (error) => {
	const config = error.config;
	if (error.response?.status === 429 && (!config._retryCount || config._retryCount < 3)) {
		config._retryCount = (config._retryCount || 0) + 1;
		const retryAfter = error.response.headers['retry-after'];
		const delay = retryAfter ? parseInt(retryAfter, 10) * 1000 : config._retryCount * 2000;
		await new Promise((resolve) => setTimeout(resolve, delay));
		return api(config);
	}
	return Promise.reject(error);
});

// User store
function _user_store() {
	let user = {
		uuid: null
	};

	const { subscribe, set } = writable(user);

	return {
		subscribe,
		set: (val) => {
			user = {
				...val,
				roles: val.roles?.map((rr) => rr.name)
			};
			set(user);
		},
		has_role: (role) => user.is_superuser || user.roles?.includes(role),
		has_any(roles) {
			if (!user.uuid) return false;
			if (!roles || roles.length == 0) return true;
			if (user.is_superuser) return true;
			return intersection(user.roles, roles).length != 0;
		}
	};
}

export const user = _user_store();

// SessionStore
export function writableSession(key, initialValue) {
	function persist(key, value) {
		sessionStorage.setItem(key, JSON.stringify(value));
	}

	const sessionValue = JSON.parse(sessionStorage.getItem(key));
	if (!sessionValue) persist(key, initialValue);

	const store = writable(sessionValue || initialValue);
	store.subscribe((value) => persist(key, value));
	return store;
}

// Dashboard location filter
function _filter_store() {
	let filter = {
		flux: '',
		uuids: undefined
	};

	const { subscribe, set } = writableSession('dashboard_filter', filter);

	return {
		subscribe,
		set: (val) => {
			const filter0 = val.uuids
				? join(
					val.uuids.map((uuid) => `r["loc_uuid"] == "${uuid}"`),
					' or '
				)
				: undefined;
			filter = { flux: filter0 ? `|> filter(fn: (r) => ${filter0})` : '', uuid: val.uuid };
			set(filter);
		}
	};
}

export const dashboard_filter = _filter_store();
export const dashboard_timespan = writableSession('dashboard_timespan', 7);

// GraphQL client
const graphql_url = '/backend/graphql/';
const graphql_ws = `${window.location.origin.replace('http', 'ws')}${graphql_url}`;

let wsClient;

let currentUserUuid;

user.subscribe((user) => {
	const uuid = user?.uuid;
	if (uuid == currentUserUuid) return;

	currentUserUuid = uuid;
	wsClient?.dispose();
	wsClient = undefined;
});

export const urqlClient = new Client({
	url: graphql_url,
	requestPolicy: 'cache-and-network',
	preferGetMethod: false, // This forces POST for queries
	exchanges: [
		cacheExchange,
		retryExchange({}),
		fetchExchange,
		subscriptionExchange({
			forwardSubscription(operation) {
				if (!wsClient)
					// Inherit graphql-ws's retry defaults: 5 attempts, randomised exponential
					// backoff, and retry only on CloseEvents. The previous overrides were
					// retryAttempts: Infinity with shouldRetry: () => true, which reconnects
					// forever on fatal errors as well -- a rejected handshake or an outage would
					// have every open tab hammering the endpoint indefinitely.
					wsClient = createClient({ url: graphql_ws });

				return {
					subscribe: (observer) => {
						const unsubscribe = wsClient.subscribe(operation, observer);
						return { unsubscribe };
					}
				};
			}
		})
	]
});

// Export function to set client in component context
export const setupUrqlClient = () => {
	setContextClient(urqlClient);
};

// Helper functions for GraphQL operations
// For compatibility, we provide both store-based and promise-based APIs

// Returns a Svelte store (for reactive usage in components)
export const queryStore = (args) => {
	return urqlQueryStore({ ...args, client: urqlClient });
};

// Smart query that returns either a store or promise based on usage
// If used with .then(), returns a promise
// If used with .subscribe(), returns a store
export const query = (args) => {
	// Create an object that has both .then() for promise-like behavior
	// and .subscribe() for store-like behavior
	return {
		// Store-like behavior
		subscribe: (handler) => {
			const store = urqlQueryStore({ ...args, client: urqlClient });
			return store.subscribe(handler);
		},

		// Promise-like behavior
		then: (onFulfilled, onRejected) => {
			const promise = new Promise((resolve) => {
				const store = urqlQueryStore({ ...args, client: urqlClient });
				// Declared before it is assigned on purpose. Svelte stores call the
				// subscriber synchronously from within subscribe(), so on that first call
				// the callback must see unsubscribe as undefined -- which the guard below
				// handles. const would put it in the temporal dead zone instead and throw
				// a ReferenceError, so prefer-const is wrong here.
				let unsubscribe;
				// eslint-disable-next-line prefer-const -- see the note above
				unsubscribe = store.subscribe((result) => {
					if (!result.fetching) {
						if (unsubscribe) unsubscribe();
						resolve(result);
					}
				});
			});
			return promise.then(onFulfilled, onRejected);
		},

		// Also add catch for promise compatibility
		catch: (onRejected) => {
			return query(args).then(undefined, onRejected);
		}
	};
};

// Mutation always returns a promise
export const mutate = async (args) => {
	const store = urqlMutationStore({ ...args, client: urqlClient });
	return new Promise((resolve) => {
		// See the note in query(): the split declaration keeps unsubscribe out of the
		// temporal dead zone during the store's synchronous first callback.
		let unsubscribe;
		// eslint-disable-next-line prefer-const -- see the note above
		unsubscribe = store.subscribe((result) => {
			if (!result.fetching) {
				if (unsubscribe) unsubscribe();
				resolve(result);
			}
		});
	});
};

// InfluxDB
const influxApi = axios.create({
	withCredentials: true,
	timeout: 10000
});

export const influxQuery = async (query) => {
	try {
		return await influxApi.post('/backend/query_influx/', query);
	} catch {
		return { data: undefined };
	}
};

export const palette = [
	'#e68091',
	'#5ec757',
	'#9f5dd2',
	'#9db935',
	'#5b6ed9',
	'#d1b33d',
	'#c94eb1',
	'#4da145',
	'#da3e7f',
	'#5dc597',
	'#dd3f4d',
	'#4dbcd8',
	'#d3532a',
	'#6286c8',
	'#de8930',
	'#815aa3',
	'#7f8a2c',
	'#d18fd0',
	'#448443',
	'#a84d7c',
	'#9fb56c',
	'#b8464a',
	'#369780',
	'#9f5528',
	'#2a6a45',
	'#de8969',
	'#556717',
	'#9c4f57',
	'#ad8a2a',
	'#627037',
	'#d5a86a',
	'#87682d'
];

// Locale
let locale, collator;

export const localeCompare = (a, b) => {
	if (!locale) {
		locale = navigatorLanguages();
		if (!isString(locale)) locale = locale[0];
		collator = new Intl.Collator(locale);
	}
	return collator.compare(a, b);
};

// Flux ranges
export const flux_ranges = [
	{
		idx: 0,
		start: 'date.sub(d: 5m, from: now())',
		stop: 'now()',
		every: 'steps',
		unit: 'second',
		step: 10,
		label: 'Last 5 minutes'
	},
	{
		idx: 1,
		start: 'date.sub(d: 10m, from: now())',
		stop: 'now()',
		every: 'steps',
		unit: 'second',
		step: 20,
		label: 'Last 10 minutes'
	},
	{
		idx: 2,
		start: 'date.sub(d: 15m, from: now())',
		stop: 'now()',
		every: 'steps',
		unit: 'second',
		step: 30,
		label: 'Last 15 minutes'
	},
	{
		idx: 3,
		start: 'date.sub(d: 1h, from: now())',
		stop: 'now()',
		every: 'steps',
		unit: 'minute',
		step: 10,
		label: 'Last 1 hour'
	},
	{
		idx: 4,
		start: 'date.sub(d: 3h, from: now())',
		stop: 'now()',
		every: 'steps',
		unit: 'minute',
		step: 30,
		label: 'Last 3 hours'
	},
	{
		idx: 5,
		start: 'date.sub(d: 6h, from: now())',
		stop: 'now()',
		every: 'steps',
		unit: 'minute',
		step: 60,
		label: 'Last 6 hours'
	},
	{
		idx: 6,
		start: 'date.sub(d: 12h, from: now())',
		stop: 'now()',
		every: 'steps',
		unit: 'hour',
		step: 2,
		label: 'Last 12 hours'
	},
	{
		idx: 7,
		start: 'date.sub(d: 1d, from: now())',
		stop: 'now()',
		every: 'steps',
		unit: 'hour',
		step: 4,
		label: 'Last 24 hours'
	},
	{
		idx: 8,
		start: 'date.sub(d: 2d, from: now())',
		stop: 'now()',
		every: 'steps',
		unit: 'hour',
		step: 8,
		label: 'Last 2 days'
	},
	{
		idx: 9,
		start: 'date.sub(d: 7d, from: now())',
		stop: 'now()',
		every: 'steps',
		unit: 'hour',
		step: 24,
		label: 'Last 7 days'
	},
	{
		idx: 10,
		start: 'date.sub(d: 30d, from: now())',
		stop: 'now()',
		every: '1d',
		unit: 'day',
		step: 3,
		label: 'Last 30 days'
	},
	{
		idx: 11,
		start: 'date.sub(d: 90d, from: now())',
		stop: 'now()',
		every: '2d',
		unit: 'day',
		step: 9,
		label: 'Last 90 days'
	},
	{
		idx: 12,
		start: 'date.sub(d: 6mo, from: now())',
		stop: 'now()',
		every: '3d',
		unit: 'day',
		step: 20,
		label: 'Last 6 months'
	},
	{
		idx: 13,
		start: 'date.sub(d: 1d, from: midnight)',
		stop: 'midnight',
		every: 'steps',
		unit: 'hour',
		step: 4,
		label: 'Yesterday'
	},
	{
		idx: 14,
		start: 'date.sub(d: 2d, from: midnight)',
		stop: 'date.sub(d: 1d, from: midnight)',
		every: 'steps',
		unit: 'hour',
		step: 4,
		label: 'Day before yesterday'
	},
	{
		idx: 15,
		start: 'date.sub(d: 7d, from: midnight)',
		stop: 'date.sub(d: 6d, from: midnight)',
		every: 'steps',
		unit: 'hour',
		step: 4,
		label: 'This day last week'
	},
	{
		idx: 16,
		start: 'date.sub(d: 1d, from: midnight)',
		stop: 'midnight',
		every: 'steps',
		unit: 'hour',
		step: 24,
		label: 'Previous week'
	},
	{
		idx: 17,
		start: 'date.sub(d: 1d, from: midnight)',
		stop: 'midnight',
		every: 'steps',
		unit: 'day',
		step: 3,
		label: 'Previous month'
	},
	{
		idx: 18,
		start: 'midnight',
		stop: 'date.add(d: 1d, to: midnight)',
		every: 'steps',
		unit: 'hour',
		step: 4,
		label: 'Today'
	},
	{
		idx: 19,
		start: 'midnight',
		stop: 'now()',
		every: 'steps',
		unit: 'hour',
		step: 4,
		label: 'Today so far'
	},
	{
		idx: 20,
		start: 'date.sub(d: 1d, from: date.truncate(t: midnight, unit: 1w, location:loc))',
		stop: 'date.add(d: 7d, to: start)',
		every: 'steps',
		unit: 'hour',
		step: 24,
		label: 'This week'
	},
	{
		idx: 21,
		start: 'date.sub(d: 1d, from: date.truncate(t: midnight, unit: 1w, location:loc))',
		stop: 'now()',
		every: 'steps',
		unit: 'hour',
		step: 24,
		label: 'This week so far'
	},
	{
		idx: 22,
		start: 'date.truncate(t: midnight, unit: 1mo, location: loc)',
		stop: 'date.add(d: 1mo, to: start)',
		every: '1d',
		unit: 'day',
		step: 3,
		label: 'This month'
	},
	{
		idx: 23,
		start: 'date.truncate(t: midnight, unit: 1mo, location: loc)',
		stop: 'now()',
		every: '1d',
		unit: 'day',
		step: 3,
		label: 'This month so far'
	},
	{
		idx: 24,
		start: 'date.truncate(t: midnight, unit: 1y, location: loc)',
		stop: 'midnight',
		every: 'steps',
		unit: 'day',
		step: 40,
		label: 'This year'
	},
	{
		idx: 25,
		start: 'date.truncate(t: midnight, unit: 1y, location: loc)',
		stop: 'now()',
		every: 'steps',
		unit: 'day',
		step: 40,
		label: 'This year so far'
	}
];

// start = date.truncate(t: now(), unit: 1d, location: loc)
// stop = date.sub(d: 1s, from: date.add(d: 1d, to: start))

// navigation helpers
export const historyBack = () => {
	history.back();
	// goto(document.referrer, { replaceState: true })
};

// formatters
export const num_format = new Intl.NumberFormat(navigator.language, {
	style: 'currency',
	currency: 'EUR'

	// These options are needed to round to whole numbers if that's what you want.
	//minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
	//maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
}).format;

export const date_format_ = new Intl.DateTimeFormat(navigator.language).format;
export const date_format = (dd) => date_format_(new Date(dd));

export const datetime_format_ = new Intl.DateTimeFormat(navigator.language, {
	dateStyle: 'medium',
	timeStyle: 'medium'
}).format;
export const datetime_format = (dd) => datetime_format_(new Date(dd));

export const date_range = ({ start, end }) => {
	return {
		from: new Date(Math.min(start, end)),
		to: new Date(Math.max(start, end))
	};
};

export const paymentTypes = [
	{ uuid: 'allow', label: 'Allow' },
	{ uuid: 'block', label: 'Block' },
	{ uuid: 'reject', label: 'Reject' }
];

export const mapPaymentType = (uuid) => {
	return paymentTypes.find((pt) => pt.uuid == uuid);
};

// Passkey helpers
// async, and it has to be: both browser checks are promises. This used to build the Promise.all,
// attach a .then that returned into nothing, and then fall through to `return false` -- so it
// answered false on every browser, the registration button it gates never rendered, and the two
// broken endpoints behind it went unnoticed for as long as they did (sip-f09).
export const passkeyAvailable = async () => {
	if (
		!window.PublicKeyCredential ||
		!window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable ||
		!window.PublicKeyCredential.isConditionalMediationAvailable
	) {
		return false;
	}
	const results = await Promise.all([
		window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable(),
		window.PublicKeyCredential.isConditionalMediationAvailable()
	]);
	return results.every((r) => r === true);
};

export const passkeyAuthenticate = async (url) => {
	const isConditionalMediationAvailable =
		window?.PublicKeyCredential?.isConditionalMediationAvailable;
	if (!isConditionalMediationAvailable) return false;
	const isCMA = await isConditionalMediationAvailable();
	if (!isCMA) return false;

	const asArrayBuffer = (v) =>
		Uint8Array.from(atob(v.replace(/_/g, '/').replace(/-/g, '+')), (c) => c.charCodeAt(0));
	const asBase64 = (ab) => btoa(String.fromCharCode(...new Uint8Array(ab)));

	const resp = await fetch(url);
	if (resp.status !== 200) {
		console.error(`Unexpected response ${resp.status}: ${await resp.text()}`);
		return false;
	}
	const payload = await resp.json();
	const abortController = new AbortController();
	const publicKeyCredentialRequestOptions = {
		challenge: asArrayBuffer(payload.challenge),
		rpId: payload.rpId
	};

	try {
		const creds = await navigator.credentials.get({
			publicKey: publicKeyCredentialRequestOptions,
			signal: abortController.signal,
			mediation: 'conditional'
		});
		const { attestationObject, clientDataJSON, signature, authenticatorData } = creds.response;
		const data = {
			id: creds.id,
			rawId: asBase64(creds.rawId),
			response: {
				attestationObject: asBase64(attestationObject),
				clientDataJSON: asBase64(clientDataJSON)
			}
		};
		if (signature) {
			data.response.signature = asBase64(signature);
			data.response.authenticatorData = asBase64(authenticatorData);
		}
		const r2 = await fetch(url, {
			method: 'POST',
			body: JSON.stringify(data),
			headers: { 'content-type': 'application/json' }
		});
		if (r2.status !== 200) {
			console.error(`Unexpected response ${r2.status}: ${await r2.text()}`);
			return false;
		}
		return true;
	} catch (err) {
		console.log('refused:', err.toString());
		return false;
	}
};

export const passkeyRegister = async (url) => {
	const asArrayBuffer = (v) =>
		Uint8Array.from(atob(v.replace(/_/g, '/').replace(/-/g, '+')), (c) => c.charCodeAt(0));

	const asBase64 = (ab) => btoa(String.fromCharCode(...new Uint8Array(ab)));

	const resp = await fetch(url);
	if (resp.status !== 200) {
		console.error(`Unexpected response ${resp.status}: ${await resp.text()}`);
		return;
	}
	const publicKeyCredentialCreationOptions = await resp.json();
	publicKeyCredentialCreationOptions.user.id = asArrayBuffer(
		publicKeyCredentialCreationOptions.user.id
	);
	publicKeyCredentialCreationOptions.challenge = asArrayBuffer(
		publicKeyCredentialCreationOptions.challenge
	);
	try {
		const creds = await navigator.credentials.create({
			publicKey: publicKeyCredentialCreationOptions
		});
		const { attestationObject, clientDataJSON, signature, authenticatorData } = creds.response;
		const data = {
			id: creds.id,
			rawId: asBase64(creds.rawId),
			response: {
				attestationObject: asBase64(attestationObject),
				clientDataJSON: asBase64(clientDataJSON)
			}
		};
		if (signature) {
			data.response.signature = asBase64(signature);
			data.response.authenticatorData = asBase64(authenticatorData);
		}
		const r2 = await fetch(url, {
			method: 'POST',
			body: JSON.stringify(data),
			headers: { 'content-type': 'application/json' }
		});
		if (r2.status !== 200) {
			console.error(`Unexpected response ${r2.status}: ${await r2.text()}`);
		}
	} catch (err) {
		console.log('refused:', err.toString());
	}
};

// pdfmake
import pdfMake from 'pdfmake/build/pdfmake';

pdfMake.fonts = {
	Roboto: {
		normal: `${window.location.origin}/assets/roboto-latin-400-normal.woff`,
		bold: `${window.location.origin}/assets/roboto-latin-700-normal.woff`,
		italics: `${window.location.origin}/assets/roboto-latin-400-italic.woff`,
		bolditalics: `${window.location.origin}/assets/roboto-latin-700-italic.woff`
	},
	Monospace: {
		normal: `${window.location.origin}/assets/roboto-mono-v13-latin-regular.woff`,
		bold: `${window.location.origin}/assets/roboto-mono-v13-latin-700.woff`,
		italics: `${window.location.origin}/assets/roboto-mono-v13-latin-italic.woff`,
		bolditalics: `${window.location.origin}/assets/roboto-mono-v13-latin-700italic.woff`
	}
};

export const createPdf = (data) => {
	const dd = {
		pageSize: 'A4',
		pageOrientation: 'portrait',
		pageMargins: 40,
		defaultStyle: {
			fontSize: 10
		},
		...data,
		styles: {
			header: {
				fontSize: 18,
				bold: true,
				alignment: 'justify'
			},
			...data.styles
		}
	};

	return pdfMake.createPdf(dd);
};

export { pdfMake };
